/**
 * Captain portal deep links (ON-426).
 *
 * The Concierge WhatsApp agent sends call-out links of the form
 * `/captain/:slug?event_id=<event_uuid>`. This module owns the URL contract
 * (`event_id`, snake_case) plus the two pieces of plumbing that need to
 * survive the login round-trip:
 *
 * - Parsing/validating the `event_id` query param without ever throwing.
 * - Stashing the requested event in sessionStorage across the OAuth redirect.
 *   `signInWithOAuth` deliberately sends `origin + pathname` as `redirectTo`
 *   (a stale `?event_id=` would collide with the `?code=` Supabase appends,
 *   and the allowed redirect patterns are path-based), so the query string is
 *   dropped on the way to Google/Apple and restored from the stash on return.
 *
 * Everything here is dependency-injected (storage, URL) so it can be unit
 * tested in a plain Node environment.
 */

/** Query-string key. Part of the public contract with stazon-api-mcp — do not rename. */
export const EVENT_ID_PARAM = 'event_id'

/** Loose RFC 4122 shape check — any version/variant, case-insensitive. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Reads `?event_id=` from a search string (`location.search` or a full URL's
 * query). Returns the lower-cased uuid, or null when absent or malformed.
 * Never throws.
 */
export function parseEventIdParam(search: string | null | undefined): string | null {
  if (!search) return null
  try {
    const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
    const raw = params.get(EVENT_ID_PARAM)?.trim()
    if (!raw || !UUID_RE.test(raw)) return null
    return raw.toLowerCase()
  } catch {
    return null
  }
}

/**
 * Returns a copy of `url` with the `event_id` query param removed, leaving
 * every other param, the hash and the path untouched. Used with
 * `history.replaceState` so a refresh after the modal opened does not
 * re-open it.
 */
export function stripEventIdFromUrl(url: string): string {
  try {
    const u = new URL(url, 'http://placeholder.invalid')
    if (!u.searchParams.has(EVENT_ID_PARAM)) return url
    u.searchParams.delete(EVENT_ID_PARAM)
    const isRelative = !/^[a-z][a-z0-9+.-]*:/i.test(url)
    return isRelative ? u.pathname + u.search + u.hash : u.toString()
  } catch {
    return url
  }
}

/** Browser wrapper for `stripEventIdFromUrl` — silent no-op outside a DOM. */
export function clearEventIdFromLocation(): void {
  if (typeof window === 'undefined' || !window.history?.replaceState) return
  const current = window.location.pathname + window.location.search + window.location.hash
  const next = stripEventIdFromUrl(current)
  if (next !== current) window.history.replaceState(window.history.state, '', next)
}

// ---------------------------------------------------------------------------
// Login round-trip stash
// ---------------------------------------------------------------------------

/** Minimal subset of the Web Storage API we rely on (lets tests pass a fake). */
export interface KeyValueStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

const STASH_PREFIX = 'stazon.captain.pendingEvent:'

export function pendingEventStorageKey(slug: string): string {
  return STASH_PREFIX + slug.toLowerCase()
}

function defaultStorage(): KeyValueStorage | null {
  try {
    if (typeof window === 'undefined') return null
    return window.sessionStorage ?? null
  } catch {
    // Accessing sessionStorage throws in some privacy modes / sandboxed iframes.
    return null
  }
}

/**
 * Remembers the requested event for `slug` before we hand the browser to an
 * OAuth provider. Swallows storage errors — losing the deep link degrades to
 * the plain list, which is the pre-ON-426 behaviour.
 */
export function stashPendingEventId(
  slug: string,
  eventId: string,
  storage: KeyValueStorage | null = defaultStorage()
): void {
  if (!storage || !slug || !eventId) return
  try {
    storage.setItem(pendingEventStorageKey(slug), eventId)
  } catch {
    /* ignore */
  }
}

/**
 * Reads the stashed event for `slug` without clearing it. Non-destructive on
 * purpose: React StrictMode double-invokes render and `useState` initialisers
 * in development, so a read-and-clear here would hand the second invocation
 * an empty stash. Callers clear it from a mount effect via
 * `clearPendingEventId`. Returns null when nothing is stashed or the value is
 * not a valid uuid.
 */
export function peekPendingEventId(
  slug: string,
  storage: KeyValueStorage | null = defaultStorage()
): string | null {
  if (!storage || !slug) return null
  try {
    const value = storage.getItem(pendingEventStorageKey(slug))
    if (!value || !UUID_RE.test(value)) return null
    return value.toLowerCase()
  } catch {
    return null
  }
}

/** Removes the stash for `slug`. Idempotent; swallows storage errors. */
export function clearPendingEventId(
  slug: string,
  storage: KeyValueStorage | null = defaultStorage()
): void {
  if (!storage || !slug) return
  try {
    storage.removeItem(pendingEventStorageKey(slug))
  } catch {
    /* ignore */
  }
}

/** Read-and-clear convenience (one-shot). Prefer peek + clear inside React. */
export function takePendingEventId(
  slug: string,
  storage: KeyValueStorage | null = defaultStorage()
): string | null {
  const value = peekPendingEventId(slug, storage)
  clearPendingEventId(slug, storage)
  return value
}

/**
 * Determines which event the portal should open on first render. The URL
 * param wins over a stash (a fresh call-out link beats an abandoned one).
 * Pure read — the caller must `clearPendingEventId` once mounted so the stash
 * cannot leak into a later visit.
 */
export function resolveRequestedEventId(
  slug: string | undefined,
  search: string,
  storage: KeyValueStorage | null = defaultStorage()
): string | null {
  const fromUrl = parseEventIdParam(search)
  const fromStash = slug ? peekPendingEventId(slug, storage) : null
  return fromUrl ?? fromStash
}

// ---------------------------------------------------------------------------
// Matching against the loaded list
// ---------------------------------------------------------------------------

export type DeepLinkOutcome =
  | { status: 'none' }
  /** Event is in the club's needs-captains list — open the modal for it. */
  | { status: 'open'; eventId: string }
  /** Event requested but not recruiting here (filled, cancelled, past, wrong club). */
  | { status: 'stale'; eventId: string }

export function matchDeepLink(
  requestedEventId: string | null,
  upcomingEvents: ReadonlyArray<{ id: string }>
): DeepLinkOutcome {
  if (!requestedEventId) return { status: 'none' }
  const hit = upcomingEvents.find((e) => e.id.toLowerCase() === requestedEventId)
  return hit ? { status: 'open', eventId: hit.id } : { status: 'stale', eventId: requestedEventId }
}

export const STALE_DEEP_LINK_MESSAGE = 'That event is no longer recruiting captains.'
